import { useCallback, useState, useEffect } from 'react'
import Checkbox from '../../../components/Checkbox'
import ApiRoute from '../../../components/ApiRoute'

import healthConditions, { getPartialList } from './health-conditions.data'

const HealthConditions = ({ activeSession, callApiFn, isApiCalling }) => {
  const [localChanges, setLocalChanges] = useState(new Set())
  const hasContact = Boolean(activeSession?.contact?.id)

  // Saved values from the API
  const savedValues = new Set((activeSession?.healthConditions || []).map(hc => hc.value))

  // Effective selection = saved XOR local toggles
  const isChecked = (id) => {
    const wasSaved = savedValues.has(id)
    const wasToggled = localChanges.has(id)
    return wasSaved !== wasToggled // XOR: toggled flips the saved state
  }

  const hasUnsavedChanges = localChanges.size > 0

  // Reset local changes when saved data changes (after GET or PUT)
  useEffect(() => {
    setLocalChanges(new Set())
  }, [activeSession?.healthConditions])

  const contactHcGet = () => {
    callApiFn({
      method: 'GET',
      route: 'contact/{id}/healthConditions',
      saveSuccessPath: 'healthConditions'
    })
  }

  const contactHcPut = useCallback(async () => {
    // Build the full list of currently checked conditions
    const allIds = healthConditions.map(hc => hc.value)
    const body = allIds
      .filter(id => isChecked(id))
      .map(id => {
        const hc = healthConditions.find(h => h.value === id)
        return hc || { value: id }
      })
    await callApiFn({
      method: 'PUT',
      route: 'contact/{id}/healthConditions',
      saveSuccessPath: 'healthConditions',
      body
    })
  }, [localChanges, activeSession?.healthConditions, callApiFn])

  const toggleSelected = useCallback((id) => {
    setLocalChanges(prev => {
      const next = new Set(prev)
      if (next.has(id)) {
        next.delete(id)
      } else {
        next.add(id)
      }
      return next
    })
  }, [])

  return (
    <div>
      <ApiRoute label='Contact Health Conditions' method='GET' route='/contact/{contactId}/healthConditions' onClick={contactHcGet} disabled={isApiCalling || !hasContact} />

      <h3>Update Health Conditions</h3>
      <div className='flex flex-wrap'>
        <div>
          {getPartialList(0).map((hc) => (
            <Checkbox
              key={hc.value}
              label={hc.label}
              type='checkbox'
              id={`hc_${hc.value}`}
              onChange={() => toggleSelected(hc.value)}
              checked={isChecked(hc.value)}
            />
          ))}
        </div>
        <div>
          {getPartialList(1).map((hc) => (
            <Checkbox
              key={hc.value}
              label={hc.label}
              type='checkbox'
              id={`hc_${hc.value}`}
              onChange={() => toggleSelected(hc.value)}
              checked={isChecked(hc.value)}
            />
          ))}
        </div>
        <div>
          {getPartialList(2).map((hc) => (
            <Checkbox
              key={hc.value}
              label={hc.label}
              type='checkbox'
              id={`hc_${hc.value}`}
              onChange={() => toggleSelected(hc.value)}
              checked={isChecked(hc.value)}
            />
          ))}
        </div>
      </div>

      {hasUnsavedChanges && (
        <div className='mb-3 border-2 border-orange-400 rounded p-2 bg-orange-50'>
          <h4 className='font-semibold mb-1'>Unsaved Changes</h4>
        </div>
      )}

      <ApiRoute method='PUT' route='/contact/{contactId}/healthConditions' onClick={contactHcPut} disabled={isApiCalling || !hasContact} />
    </div>
  )
}

export default HealthConditions
